import { Button } from 'primereact/button'
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { MultiSelect } from 'primereact/multiselect'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { useGetProjects } from '../../api/project-API'
import { CloseIcon, FilterIcon, GroupIcon, ListIcon } from '../../components/Icons'
import { useDebounce } from '../../hooks/useDebounce'
import { protoService } from '../../proto/ProtoService'
import { Project, RunStatus } from '../../types/types'
import { queryString2JsonString } from '../../types/util'
import { SubmissionStatusList } from '../../utils/constants'
import { currentUserState, vehicleListState } from '../../utils/state-atoms'
import { CheckboxStyle } from '../../utils/styling-constants'

type SubmissionFilter = {
  vehicle?: string
  project?: string
  byMeOnly?: string
  status?: string | string[]
  caseNumber?: number
}

type Props = {
  onFilterChange: (formattedQuery: string) => void
  onSwitchViewMode: (mode: string) => void
}

const SUBMISSION_FILTER_KEY = 'SUBMISSION_FILTER'
export const SUBMISSION_VIEW_MODE_KEY = 'SUBMISSION_VIEW_MODE'

const savedViewMode: 'run' | 'case' =
  localStorage.getItem(SUBMISSION_VIEW_MODE_KEY) === 'case' ? 'case' : 'run'

const savedFilterString = localStorage.getItem(SUBMISSION_FILTER_KEY)
let savedFilter: SubmissionFilter = {}
if (savedFilterString) {
  savedFilter = JSON.parse(savedFilterString) as SubmissionFilter
}

const normalizeStatusFilter = (status: SubmissionFilter['status']): string[] => {
  if (!status) return []
  return Array.isArray(status) ? status : [status]
}

const SubmissionFilters: FC<Props> = ({ onFilterChange, onSwitchViewMode }) => {
  const currentUser = useRecoilValue(currentUserState)
  const vehicleList = useRecoilValue(vehicleListState)

  const [viewMode, setViewMode] = useState<'run' | 'case'>(savedViewMode)
  const [byMeOnly, setByMeOnly] = useState(savedFilter ? savedFilter.byMeOnly === 'true' : false)
  const [filterVehicle, setFilterVehicle] = useState<string | undefined>(
    savedFilter ? savedFilter.vehicle : undefined
  )
  const [filterProject, setFilterProject] = useState<string | undefined>(
    savedFilter ? savedFilter.project : ''
  )
  const [filterStatus, setFilterStatus] = useState<string[]>(
    normalizeStatusFilter(savedFilter?.status)
  )
  const [filterCaseNumber, setFilterCaseNumber] = useState<number | null>(
    savedFilter?.caseNumber ?? null
  )
  const debouncedCaseNumber = useDebounce(filterCaseNumber, 500)
  const [filterOpen, setFilterOpen] = useState(
    savedFilter &&
      (!!savedFilter.status ||
        !!savedFilter.vehicle ||
        !!savedFilter.project ||
        !!savedFilter.caseNumber)
  )

  const [{ data: projectsData, loading: loadingProjects }, fetchProjects] = useGetProjects()
  const lastFormattedQueryRef = useRef<string | null>(null)
  const [projectList, setProjectList] = useState<Project[]>([])

  useEffect(() => {
    if (!filterVehicle) {
      setProjectList([])
      setFilterProject(undefined)
      return
    }

    const query = 'simple=true&query=' + queryString2JsonString(`vehicleId=${filterVehicle}`)
    fetchProjects(query).catch(() => {
      // keep quiet in filter component; parent will handle submission fetch errors
    })
  }, [fetchProjects, filterVehicle])

  useEffect(() => {
    if (!projectsData) {
      setProjectList([])
      return
    }

    const projs = protoService.decodeProtoResponse(projectsData, 'project')
    setProjectList((projs as Project[]) || [])
  }, [projectsData])

  const statusNames = useMemo(() => {
    return filterStatus
      .map(status => {
        const numericValue = Number(status)
        if (!Number.isNaN(numericValue)) {
          return RunStatus[numericValue as RunStatus] || status
        }
        return status
      })
      .filter(Boolean)
  }, [filterStatus])

  useEffect(() => {
    const queryObj: Record<string, unknown> = {}

    if (byMeOnly && currentUser?._id) {
      queryObj.user = currentUser._id
    }

    if (statusNames.length > 0) {
      queryObj.overallStatus = statusNames.join(',')
    }

    if (debouncedCaseNumber !== null) {
      queryObj.systemRunId = debouncedCaseNumber
    }

    if (filterProject) {
      queryObj.projectId = filterProject
    } else if (filterVehicle) {
      queryObj.vehicleId = filterVehicle
    }

    const formattedQuery =
      Object.keys(queryObj).length > 0
        ? `query=${encodeURIComponent(JSON.stringify(queryObj))}`
        : ''

    if (lastFormattedQueryRef.current === formattedQuery) {
      return
    }

    lastFormattedQueryRef.current = formattedQuery
    onFilterChange(formattedQuery)
  }, [
    byMeOnly,
    currentUser?._id,
    debouncedCaseNumber,
    filterVehicle,
    filterProject,
    onFilterChange,
    statusNames
  ])

  const persistFilter = (next: SubmissionFilter) => {
    localStorage.setItem(SUBMISSION_FILTER_KEY, JSON.stringify(next))
  }

  return (
    <>
      <div className="basis-0 flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-3 rounded-md">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => setFilterOpen(!filterOpen)}
            className="dark-button !w-fit items-center !p-2 text-sm"
            label={filterOpen ? 'Hide Filters' : 'Show Filters'}
            icon={<FilterIcon className="fill-white h-4 w-4" />}
          />
          <div className="flex items-center border-l border-gray-300 dark:border-gray-600 pl-4">
            <Checkbox
              inputId="byMeOnly"
              pt={CheckboxStyle}
              onChange={(evt: CheckboxChangeEvent) => {
                const next = evt.checked as boolean
                setByMeOnly(next)
                const updated = {
                  ...savedFilter,
                  byMeOnly: next ? 'true' : 'false',
                  vehicle: filterVehicle,
                  project: filterProject,
                  status: filterStatus
                }
                savedFilter = updated
                persistFilter(updated)
              }}
              checked={byMeOnly}></Checkbox>
            <label htmlFor="byMeOnly" className="ml-2 font-normal">
              Submitted by me only
            </label>
          </div>
          {filterOpen && (
            <button
              onClick={() => {
                setFilterVehicle(undefined)
                setFilterProject(undefined)
                setFilterStatus([])
                setFilterCaseNumber(null)
                setFilterOpen(false)
                const updated = {
                  ...savedFilter,
                  vehicle: undefined,
                  project: undefined,
                  status: undefined,
                  caseNumber: undefined
                }
                savedFilter = updated
                persistFilter(updated)
              }}
              className="!bg-transparent !w-fit flex items-center !p-2 text-sm">
              <CloseIcon className="fill-neutral-70 dark:fill-neutral-60 h-4 w-4" />
              <span className="text-neutral-70 dark:text-neutral-60 pt-[2px] hover:text-neutral-90 dark:hover:text-neutral-20">
                Clear Filters
              </span>
            </button>
          )}
        </div>

        <div className="flex basis-0 grow items-center justify-end space-x-2">
          <button
            className={classNames('p-3 rounded-lg border-none flex items-center justify-center', {
              'bg-gm-aqua-light dark:bg-gm-aqua-dark hover:bg-gm-aqua-dark dark:hover:bg-gm-aqua-light':
                viewMode === 'run',
              'bg-gray-100 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600':
                viewMode === 'case'
            })}
            onClick={() => {
              setViewMode('run')
              localStorage.setItem(SUBMISSION_VIEW_MODE_KEY, 'run')
              onSwitchViewMode('run')
            }}>
            <GroupIcon
              className={classNames('w-4 h-4', {
                'fill-white dark:fill-neutral-20': viewMode === 'run',
                'fill-neutral-70 dark:fill-gray-100': viewMode === 'case'
              })}
            />
          </button>
          <button
            className={classNames('p-3 rounded-lg border-none flex items-center justify-center', {
              'bg-gm-aqua-light dark:bg-gm-aqua-dark hover:bg-gm-aqua-dark dark:hover:bg-gm-aqua-light':
                viewMode === 'case',
              'bg-gray-100 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600':
                viewMode === 'run'
            })}
            onClick={() => {
              setViewMode('case')
              setFilterOpen(false)
              localStorage.setItem(SUBMISSION_VIEW_MODE_KEY, 'case')
              onSwitchViewMode('case')
            }}>
            <ListIcon
              className={classNames('w-4 h-4 ', {
                'fill-white dark:fill-neutral-20': viewMode === 'case',
                'fill-neutral-70 dark:fill-gray-100': viewMode === 'run'
              })}
            />
          </button>
        </div>
      </div>

      {filterOpen && (
        <div className="flex items-end p-4 space-x-2 mt-2 bg-gray-50 dark:bg-gray-800 rounded-md animate-fade-in-down">
          <div className="flex flex-col items-stretch basis-0 grow space-y-2">
            <label>Car</label>
            <Dropdown
              value={filterVehicle}
              showClear
              pt={{
                root: {
                  className: 'min-w-full'
                },
                filterInput: {
                  className: '!py-1'
                },
                input: {
                  className: '!py-2'
                }
              }}
              onChange={e => {
                setFilterVehicle(e.value)
                setFilterProject(undefined)

                const updated = {
                  ...savedFilter,
                  vehicle: e.value,
                  project: undefined,
                  status: filterStatus,
                  byMeOnly: byMeOnly ? 'true' : 'false'
                }
                savedFilter = updated
                persistFilter(updated)
              }}
              placeholder="Select a Car"
              filter
              options={vehicleList}
              optionValue="_id"
              optionLabel="name"
              emptyMessage="&nbsp;&nbsp;No data"
            />
          </div>

          {projectList && projectList.length > 0 && (
            <div className="flex flex-col items-stretch basis-0 grow space-y-2">
              <label>Project</label>
              <Dropdown
                pt={{
                  root: {
                    className: 'min-w-full'
                  },
                  filterInput: {
                    className: '!py-1'
                  },
                  input: {
                    className: '!py-2'
                  }
                }}
                onChange={evt => {
                  setFilterProject(evt.value)

                  const updated = {
                    ...savedFilter,
                    vehicle: filterVehicle,
                    project: evt.value,
                    status: filterStatus,
                    byMeOnly: byMeOnly ? 'true' : 'false'
                  }
                  savedFilter = updated
                  persistFilter(updated)
                }}
                showClear
                filter
                loading={loadingProjects}
                filterBy="name"
                value={filterProject}
                options={projectList}
                optionLabel="name"
                optionValue="_id"
                placeholder="Select Project"
                itemTemplate={item => {
                  return `${item.name}`
                }}
              />
            </div>
          )}

          <div className="flex flex-col items-stretch basis-0 grow-2 space-y-2">
            <label>Status</label>
            <MultiSelect
              pt={{
                root: {
                  className: 'min-w-full'
                },
                input: {
                  className: '!py-2'
                },
                label: {
                  className: '!py-[10px]'
                },
                headerSelectAllLabel: {
                  className: 'mx-2 wrap whitespace-nowrap pt-[2px]'
                },
                header: {
                  className: '!p-2'
                },
                list: {
                  className: 'pt-0'
                },
                token: {
                  className: '!py-0 !text-sm'
                }
              }}
              selectAllLabel="Select All"
              onChange={evt => {
                const nextStatuses = (evt.value || []) as string[]
                setFilterStatus(nextStatuses)

                const updated = {
                  ...savedFilter,
                  vehicle: filterVehicle,
                  project: filterProject,
                  status: nextStatuses,
                  byMeOnly: byMeOnly ? 'true' : 'false'
                }
                savedFilter = updated
                persistFilter(updated)
              }}
              value={filterStatus}
              options={SubmissionStatusList}
              display="chip"
              showClear
              placeholder="Select Status(es)"
              optionValue="value"
              optionLabel="name"
            />
          </div>

          <div className="flex flex-col items-stretch basis-0 grow space-y-2">
            <label>Case #</label>
            <InputNumber
              value={filterCaseNumber}
              onChange={evt => {
                const nextCaseNumber = evt.value ?? null
                setFilterCaseNumber(nextCaseNumber)

                const updated = {
                  ...savedFilter,
                  vehicle: filterVehicle,
                  project: filterProject,
                  status: filterStatus,
                  caseNumber: nextCaseNumber ?? undefined,
                  byMeOnly: byMeOnly ? 'true' : 'false'
                }
                savedFilter = updated
                persistFilter(updated)
              }}
              placeholder="Enter Case #"
              useGrouping={false}
              min={0}
              inputClassName="w-full"
            />
          </div>
        </div>
      )}
    </>
  )
}

export default SubmissionFilters
